import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Paintbrush, Palette, Search, Code, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INPUT = "w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20";
const LABEL = "block text-xs font-medium text-stone-600 mb-1";

const FONT_OPTIONS = [
  'Manrope',
  'Inter',
  'Poppins',
  'DM Sans',
  'Plus Jakarta Sans',
  'Playfair Display',
  'Lora',
];

const TABS = [
  { key: 'branding', label: 'Branding', icon: Paintbrush },
  { key: 'colors', label: 'Colors & Fonts', icon: Palette },
  { key: 'seo', label: 'SEO', icon: Search },
  { key: 'advanced', label: 'Advanced', icon: Code },
];

export default function GlobalStylesPanel({ site, siteId, getAuthHeader, onSiteUpdate }) {
  const [activeTab, setActiveTab] = useState('branding');
  const [saving, setSaving] = useState(false);

  // Local state mirrors site data for editing
  const [branding, setBranding] = useState({
    logo_url: '',
    company_name: '',
    favicon_url: '',
  });
  const [settings, setSettings] = useState({
    primary_color: '#1c1917',
    accent_color: '#f59e0b',
    background_color: '#FAFAF5',
    default_font_heading: 'Manrope',
    default_font_body: 'Inter',
    show_branding: true,
    google_analytics_id: '',
    facebook_pixel_id: '',
    custom_head_code: '',
  });
  const [seo, setSeo] = useState({
    title: '',
    description: '',
    og_image: '',
    robots: 'index,follow',
  });

  // Initialize from site data
  useEffect(() => {
    if (!site) return;
    const b = site.branding || {};
    setBranding({
      logo_url: b.logo_url || '',
      company_name: b.company_name || '',
      favicon_url: b.favicon_url || b.favicon_asset_id || '',
    });
    const s = site.settings || {};
    setSettings({
      primary_color: s.primary_color || '#1c1917',
      accent_color: s.accent_color || '#f59e0b',
      background_color: s.background_color || '#FAFAF5',
      default_font_heading: s.default_font_heading || s.font_heading || 'Manrope',
      default_font_body: s.default_font_body || s.font_body || 'Inter',
      show_branding: s.show_branding !== undefined ? s.show_branding : true,
      google_analytics_id: s.google_analytics_id || '',
      facebook_pixel_id: s.facebook_pixel_id || '',
      custom_head_code: s.custom_head_code || '',
    });
    const se = site.seo || {};
    setSeo({
      title: se.title || '',
      description: se.description || '',
      og_image: se.og_image || se.og_image_asset_id || '',
      robots: se.robots || 'index,follow',
    });
  }, [site]);

  const saveChanges = useCallback(async (updates) => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/sites/${siteId}`, updates, { headers: getAuthHeader() });
      if (onSiteUpdate) onSiteUpdate(res.data);
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [siteId, getAuthHeader, onSiteUpdate]);

  // Debounced auto-save
  const autoSaveRef = React.useRef(null);
  const debouncedSave = useCallback((updates) => {
    if (autoSaveRef.current) clearTimeout(autoSaveRef.current);
    autoSaveRef.current = setTimeout(() => saveChanges(updates), 800);
  }, [saveChanges]);

  // Change handlers that auto-save
  const updateBranding = (key, value) => {
    const updated = { ...branding, [key]: value };
    setBranding(updated);
    debouncedSave({ branding: updated });
  };

  const updateSettings = (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    debouncedSave({ settings: updated });
  };

  const updateSeo = (key, value) => {
    const updated = { ...seo, [key]: value };
    setSeo(updated);
    debouncedSave({ seo: updated });
  };

  return (
    <>
      {/* Dark overlay */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={() => onSiteUpdate && onSiteUpdate(null, true)} />

      {/* Slide-over panel */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-stone-900">Site Settings</h2>
            {saving && <Loader2 className="w-3.5 h-3.5 text-stone-400 animate-spin" />}
          </div>
          <button
            onClick={() => onSiteUpdate && onSiteUpdate(null, true)}
            className="p-1.5 rounded-md text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-200 px-2">
          {TABS.map(({ key, label, icon: TabIcon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-stone-900 text-stone-900'
                  : 'border-transparent text-stone-400 hover:text-stone-600'
              }`}
            >
              <TabIcon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'branding' && (
            <div className="space-y-5">
              <div>
                <label className={LABEL}>Company Name</label>
                <input
                  value={branding.company_name}
                  onChange={(e) => updateBranding('company_name', e.target.value)}
                  className={INPUT}
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label className={LABEL}>Logo URL</label>
                <input
                  value={branding.logo_url}
                  onChange={(e) => updateBranding('logo_url', e.target.value)}
                  className={INPUT}
                  placeholder="https://..."
                />
                {branding.logo_url && (
                  <div className="mt-2 p-3 bg-stone-50 rounded-lg border border-stone-200 flex items-center justify-center">
                    <img
                      src={branding.logo_url}
                      alt="Logo preview"
                      className="max-h-12 max-w-[200px] object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className={LABEL}>Favicon URL</label>
                <input
                  value={branding.favicon_url}
                  onChange={(e) => updateBranding('favicon_url', e.target.value)}
                  className={INPUT}
                  placeholder="https://... (32x32 .ico or .png)"
                />
                {branding.favicon_url && (
                  <div className="mt-2 flex items-center gap-2">
                    <img
                      src={branding.favicon_url}
                      alt="Favicon preview"
                      className="w-4 h-4"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <span className="text-[10px] text-stone-400">Preview</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'colors' && (
            <div className="space-y-5">
              {/* Color Pickers */}
              {[
                { key: 'primary_color', label: 'Primary Color', desc: 'Main brand color for buttons and links' },
                { key: 'accent_color', label: 'Accent Color', desc: 'Used for highlights and secondary CTAs' },
                { key: 'background_color', label: 'Background Color', desc: 'Default page background' },
              ].map(({ key, label, desc }) => (
                <div key={key}>
                  <label className={LABEL}>{label}</label>
                  <p className="text-[10px] text-stone-400 mb-1.5">{desc}</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={settings[key]}
                      onChange={(e) => updateSettings(key, e.target.value)}
                      className="w-10 h-10 rounded-lg border border-stone-200 cursor-pointer p-0.5"
                    />
                    <input
                      value={settings[key]}
                      onChange={(e) => updateSettings(key, e.target.value)}
                      className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white font-mono"
                    />
                  </div>
                </div>
              ))}

              <div className="w-full h-px bg-stone-200 my-2" />

              {/* Font Selectors */}
              <div>
                <label className={LABEL}>Heading Font</label>
                <select
                  value={settings.default_font_heading}
                  onChange={(e) => updateSettings('default_font_heading', e.target.value)}
                  className={INPUT}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={LABEL}>Body Font</label>
                <select
                  value={settings.default_font_body}
                  onChange={(e) => updateSettings('default_font_body', e.target.value)}
                  className={INPUT}
                >
                  {FONT_OPTIONS.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              {/* Font Preview */}
              <div className="p-4 bg-stone-50 rounded-lg border border-stone-200">
                <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-3">Font Preview</p>
                <p
                  className="text-lg font-bold text-stone-900 mb-1"
                  style={{ fontFamily: `'${settings.default_font_heading}', sans-serif` }}
                >
                  Heading Font: {settings.default_font_heading}
                </p>
                <p
                  className="text-sm text-stone-600"
                  style={{ fontFamily: `'${settings.default_font_body}', sans-serif` }}
                >
                  Body text uses {settings.default_font_body}. The quick brown fox jumps over the lazy dog.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-5">
              <div>
                <label className={LABEL}>Site Title</label>
                <input
                  value={seo.title}
                  onChange={(e) => updateSeo('title', e.target.value)}
                  className={INPUT}
                  placeholder="My Awesome Site"
                />
              </div>

              <div>
                <label className={LABEL}>Meta Description</label>
                <textarea
                  value={seo.description}
                  onChange={(e) => updateSeo('description', e.target.value)}
                  className={INPUT}
                  rows={3}
                  placeholder="A brief description of your site for search engines..."
                  maxLength={160}
                />
                <div className="flex justify-between mt-1">
                  <p className="text-[10px] text-stone-400">Recommended: 120-160 characters</p>
                  <p className={`text-[10px] font-medium ${
                    (seo.description || '').length > 160 ? 'text-red-500' :
                    (seo.description || '').length > 120 ? 'text-green-500' : 'text-stone-400'
                  }`}>
                    {(seo.description || '').length}/160
                  </p>
                </div>
              </div>

              <div>
                <label className={LABEL}>OG Image URL</label>
                <input
                  value={seo.og_image}
                  onChange={(e) => updateSeo('og_image', e.target.value)}
                  className={INPUT}
                  placeholder="https://... (1200x630 recommended)"
                />
                {seo.og_image && (
                  <div className="mt-2 p-2 bg-stone-50 rounded-lg border border-stone-200">
                    <img
                      src={seo.og_image}
                      alt="OG preview"
                      className="w-full h-32 object-cover rounded"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>

              <div>
                <label className={LABEL}>Robots</label>
                <select
                  value={seo.robots}
                  onChange={(e) => updateSeo('robots', e.target.value)}
                  className={INPUT}
                >
                  <option value="index,follow">index, follow (recommended)</option>
                  <option value="noindex,nofollow">noindex, nofollow</option>
                  <option value="index,nofollow">index, nofollow</option>
                  <option value="noindex,follow">noindex, follow</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="space-y-5">
              <div>
                <label className={LABEL}>Google Analytics ID</label>
                <input
                  value={settings.google_analytics_id}
                  onChange={(e) => updateSettings('google_analytics_id', e.target.value)}
                  className={INPUT}
                  placeholder="G-XXXXXXXXXX"
                />
              </div>

              <div>
                <label className={LABEL}>Facebook Pixel ID</label>
                <input
                  value={settings.facebook_pixel_id}
                  onChange={(e) => updateSettings('facebook_pixel_id', e.target.value)}
                  className={INPUT}
                  placeholder="1234567890"
                />
              </div>

              <div className="w-full h-px bg-stone-200 my-2" />

              <div>
                <label className={LABEL}>Custom Head Code</label>
                <p className="text-[10px] text-stone-400 mb-1.5">
                  Injected into the &lt;head&gt; of every published page
                </p>
                <textarea
                  value={settings.custom_head_code}
                  onChange={(e) => updateSettings('custom_head_code', e.target.value)}
                  className={`${INPUT} font-mono text-xs`}
                  rows={6}
                  placeholder="<script>...</script>"
                />
              </div>

              <div className="w-full h-px bg-stone-200 my-2" />

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-stone-700">Show NurtureCRM branding</p>
                  <p className="text-[10px] text-stone-400 mt-0.5">
                    Display "Powered by NurtureCRM" on published pages
                  </p>
                </div>
                <button
                  onClick={() => updateSettings('show_branding', !settings.show_branding)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    settings.show_branding ? 'bg-stone-900' : 'bg-stone-300'
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    settings.show_branding ? 'translate-x-5' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
